import { type ContentRect } from '@guidde/design-system'
import { useEffect, useState, type RefObject } from 'react'

export type { ContentRect }

/**
 * Finds the `<video>` element rendered by the DS player inside `containerRef`. It may not exist on
 * first paint, so poll until it appears.
 */
export const useVideoElement = (containerRef: RefObject<HTMLElement>): HTMLVideoElement | null => {
    const [video, setVideo] = useState<HTMLVideoElement | null>(null)

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const existing = container.querySelector('video')
        if (existing) {
            setVideo(existing)
            return
        }

        let pollId = 0
        const poll = () => {
            const element = container.querySelector('video')
            if (element) {
                setVideo(element)
                return
            }
            pollId = window.setTimeout(poll, 100)
        }
        poll()

        return () => window.clearTimeout(pollId)
    }, [containerRef])

    return video
}

/**
 * Computes the on-screen rectangle occupied by the video *content*, relative to `containerRef`,
 * using the frame aspect ratio (from the step's windowDimensions) rather than the video's intrinsic
 * size — so it works BEFORE playback / metadata load (when videoWidth is still 0). The player uses
 * `object-fit: contain`, so the content is letterboxed inside its box; this returns the true content
 * box. Prefers the `<video>` element's box (correct in fullscreen); falls back to the container box
 * (aspect-locked) while the video element has no layout size yet. Recomputes on resize / fullscreen.
 */
export const useVideoContentRect = (
    video: HTMLVideoElement | null,
    // The element the overlay is positioned within (the player box, or the fullscreen element while
    // fullscreen). Rect is returned relative to it.
    container: HTMLElement | null,
    frameSize: { width: number; height: number } | null,
): ContentRect | null => {
    const [rect, setRect] = useState<ContentRect | null>(null)

    const frameAspect = frameSize && frameSize.height ? frameSize.width / frameSize.height : null

    useEffect(() => {
        if (!container || !frameAspect) return

        let rafId = 0

        const measure = () => {
            const videoRect = video?.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()

            // Prefer the video element's box (accurate in fullscreen); fall back to the container
            // box while the video has no layout size yet (before metadata / first paint).
            const box = videoRect && videoRect.width > 0 && videoRect.height > 0 ? videoRect : containerRect
            if (!box.width || !box.height) return

            // object-fit: contain of the frame aspect within the box → letterbox / pillarbox padding
            const boxAspect = box.width / box.height
            const contentW = boxAspect > frameAspect ? box.height * frameAspect : box.width
            const contentH = boxAspect > frameAspect ? box.height : box.width / frameAspect

            setRect({
                left: box.left - containerRect.left + (box.width - contentW) / 2,
                top: box.top - containerRect.top + (box.height - contentH) / 2,
                width: contentW,
                height: contentH,
            })
        }

        const scheduleMeasure = () => {
            cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(measure)
        }

        video?.addEventListener('loadedmetadata', scheduleMeasure)
        video?.addEventListener('resize', scheduleMeasure)
        window.addEventListener('resize', scheduleMeasure)
        document.addEventListener('fullscreenchange', scheduleMeasure)

        const resizeObserver = new ResizeObserver(scheduleMeasure)
        if (video) resizeObserver.observe(video)
        resizeObserver.observe(container)

        scheduleMeasure()

        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', scheduleMeasure)
            document.removeEventListener('fullscreenchange', scheduleMeasure)
            resizeObserver.disconnect()
            video?.removeEventListener('loadedmetadata', scheduleMeasure)
            video?.removeEventListener('resize', scheduleMeasure)
        }
    }, [video, container, frameAspect])

    return rect
}
